import * as THREE from 'three'
import type { InputFrame } from '@/input/types'
import type { CameraMode, MapManifest } from '@/gameplay/types'

const EYE_HEIGHT = 1.7
const RADIUS = 0.35
/** Заметно медленнее прежних 5.5 — читаемый step-reveal. */
const SPEED = 2.75
const PITCH_LIMIT = Math.PI / 2 - 0.08
/** TPP: длина spring-arm и смещение за плечо */
const TPP_ARM = 3.6
const TPP_SHOULDER = 0.6
const TPP_HEIGHT = 0.45
/** Deadzone стика — отсекает шум около центра */
const MOVE_DEADZONE = 0.05

export class FpsController {
  readonly camera: THREE.PerspectiveCamera
  /** Low-poly operator — виден только в TPP. */
  readonly body: THREE.Group
  position = new THREE.Vector3()
  yaw = 0
  pitch = 0
  private walls: Array<{ minX: number; maxX: number; minZ: number; maxZ: number }> = []
  private bounds = { minX: -50, maxX: 50, minZ: -50, maxZ: 50 }
  private sensitivity = 1
  private invertY = false
  private cameraMode: CameraMode = 'fps'

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(70, aspect, 0.05, 200)
    this.body = buildPlayerMesh()
    this.body.visible = false
  }

  setLookSettings(sensitivity: number, invertY: boolean): void {
    this.sensitivity = sensitivity
    this.invertY = invertY
  }

  setCameraMode(mode: CameraMode): void {
    this.cameraMode = mode
    this.body.visible = mode === 'tpp'
    this.syncCamera()
  }

  getCameraMode(): CameraMode {
    return this.cameraMode
  }

  resetFromMap(map: MapManifest, cameraMode: CameraMode = 'fps'): void {
    this.position.set(map.spawn.position.x, EYE_HEIGHT, map.spawn.position.z)
    this.yaw = map.spawn.yaw
    this.pitch = 0
    this.cameraMode = cameraMode
    this.body.visible = cameraMode === 'tpp'
    this.bounds = { ...map.bounds }
    // Стены + solid props — единый список AABB
    const colliders = [...map.walls, ...(map.props ?? [])]
    this.walls = colliders.map((w) => ({
      minX: w.x - w.w / 2 - RADIUS,
      maxX: w.x + w.w / 2 + RADIUS,
      minZ: w.z - w.d / 2 - RADIUS,
      maxZ: w.z + w.d / 2 + RADIUS,
    }))
    this.syncCamera()
    this.syncBody()
  }

  integrate(dt: number, input: InputFrame): void {
    const lookScale = 0.0022 * this.sensitivity
    this.yaw -= input.lookDelta.x * lookScale
    const ySign = this.invertY ? 1 : -1
    this.pitch += input.lookDelta.y * lookScale * ySign
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch))

    // Analog: скорость = SPEED * |move| (stick magnitude / WASD после clamp)
    const mag = Math.min(1, Math.hypot(input.move.x, input.move.y))
    if (mag > MOVE_DEADZONE) {
      const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw))
      const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw))
      const wish = new THREE.Vector3()
      wish.addScaledVector(forward, input.move.y)
      wish.addScaledVector(right, input.move.x)
      if (wish.lengthSq() > 0) {
        wish.normalize().multiplyScalar(SPEED * mag * dt)
        this.moveWithCollisions(wish)
      }
    }

    this.position.y = EYE_HEIGHT
    this.syncBody()
    this.syncCamera()
  }

  private moveWithCollisions(delta: THREE.Vector3): void {
    const next = this.position.clone().add(delta)
    next.x = Math.max(this.bounds.minX + RADIUS, Math.min(this.bounds.maxX - RADIUS, next.x))
    next.z = Math.max(this.bounds.minZ + RADIUS, Math.min(this.bounds.maxZ - RADIUS, next.z))

    // Разделяем оси — проще kinematic AABB
    const tryX = this.position.clone()
    tryX.x = next.x
    if (!this.hitsWall(tryX.x, this.position.z)) this.position.x = tryX.x

    const tryZ = this.position.clone()
    tryZ.z = next.z
    if (!this.hitsWall(this.position.x, tryZ.z)) this.position.z = tryZ.z
  }

  private hitsWall(x: number, z: number): boolean {
    for (const w of this.walls) {
      if (x >= w.minX && x <= w.maxX && z >= w.minZ && z <= w.maxZ) return true
    }
    return false
  }

  /** Укоротить arm, если луч к камере пересекает wall AABB. */
  private shortenArm(desired: THREE.Vector3): THREE.Vector3 {
    const origin = this.position.clone()
    const toCam = desired.clone().sub(origin)
    const dist = toCam.length()
    if (dist < 0.01) return desired
    const dir = toCam.normalize()

    let maxT = dist
    for (const w of this.walls) {
      const t = rayAabbT(origin.x, origin.z, dir.x, dir.z, w)
      if (t !== null && t > 0.2 && t < maxT) maxT = t
    }
    // Небольшой зазор от стены
    maxT = Math.max(0.5, maxT - 0.25)
    return origin.clone().addScaledVector(dir, maxT)
  }

  private syncBody(): void {
    // Ноги на полу; yaw как у камеры
    this.body.position.set(this.position.x, 0, this.position.z)
    this.body.rotation.y = this.yaw
  }

  private syncCamera(): void {
    const euler = new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ')
    this.camera.quaternion.setFromEuler(euler)

    if (this.cameraMode === 'fps') {
      this.camera.position.copy(this.position)
      return
    }

    // TPP: spring-arm за плечом, тот же look
    const back = new THREE.Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw))
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw))
    const desired = this.position
      .clone()
      .addScaledVector(back, TPP_ARM)
      .addScaledVector(right, TPP_SHOULDER)
    desired.y = this.position.y + TPP_HEIGHT - this.pitch * 0.4

    this.camera.position.copy(this.shortenArm(desired))
  }

  getLookRay(): THREE.Raycaster {
    const ray = new THREE.Raycaster()
    ray.setFromCamera(new THREE.Vector2(0, 0), this.camera)
    return ray
  }
}

/** Капсула + простой торс — силуэт operator для TPP. */
function buildPlayerMesh(): THREE.Group {
  const root = new THREE.Group()
  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x3a434c,
    roughness: 0.75,
    metalness: 0.15,
  })
  const accentMat = new THREE.MeshStandardMaterial({
    color: 0x2a2e33,
    roughness: 0.65,
    metalness: 0.2,
  })

  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 0.7, 4, 8), bodyMat)
  torso.position.y = 1.05
  torso.castShadow = true
  root.add(torso)

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 8), accentMat)
  head.position.y = 1.72
  head.castShadow = true
  root.add(head)

  const vest = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.45, 0.28), accentMat)
  vest.position.y = 1.15
  vest.castShadow = true
  root.add(vest)

  return root
}

/** Пересечение луча XZ с AABB; возвращает t или null. */
function rayAabbT(
  ox: number,
  oz: number,
  dx: number,
  dz: number,
  box: { minX: number; maxX: number; minZ: number; maxZ: number },
): number | null {
  let tMin = 0
  let tMax = Infinity

  if (Math.abs(dx) < 1e-8) {
    if (ox < box.minX || ox > box.maxX) return null
  } else {
    let t1 = (box.minX - ox) / dx
    let t2 = (box.maxX - ox) / dx
    if (t1 > t2) [t1, t2] = [t2, t1]
    tMin = Math.max(tMin, t1)
    tMax = Math.min(tMax, t2)
    if (tMin > tMax) return null
  }

  if (Math.abs(dz) < 1e-8) {
    if (oz < box.minZ || oz > box.maxZ) return null
  } else {
    let t1 = (box.minZ - oz) / dz
    let t2 = (box.maxZ - oz) / dz
    if (t1 > t2) [t1, t2] = [t2, t1]
    tMin = Math.max(tMin, t1)
    tMax = Math.min(tMax, t2)
    if (tMin > tMax) return null
  }

  return tMin >= 0 ? tMin : tMax >= 0 ? tMax : null
}
